import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { STUDIO_THEMES, type StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { fymAPI } from '../../../../redux/api/fymAPI';
import { StoreType } from '../../../../redux/store';
import { showToast } from '../../../../redux/slices/toasts/slice';
import { ToastType } from '../../../../redux/slices/toasts/state';
import { registerToastAction } from '../../../../redux/slices/toasts/toastActions';
import { useRealtimeEvent } from '../../../realtime/useRealtimeEvent';
import { studioThemeMessages } from '../studio/studioThemeMessages';
import { PublishDialog } from './PublishDialog';
import {
  openPublishDialog,
  PublishWatch,
  readPublishWatches,
  removePublishWatch,
  usePublishDialogRequest,
} from './publishDialogStore';
import { publishMessages } from './publishMessages';

const ACTION_PREFIX = 'publish-ready:';

/**
 * Draws the publish dialog for the whole app (mounted once in `SMLContainer`), and shows the
 * "Publish to YouTube?" toast when a full-song video that the user made with the Studio switch off
 * turns ready (owner decision 2026-10-09). The socket event `kit.asset.updated` holds ids only, so
 * the host reads the release kit of the song once to see the new state.
 */
const PublishHostComponent: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch<StoreType['dispatch']>();
  const request = usePublishDialogRequest();

  const assetName = useCallback(
    (themeId: string) =>
      themeId in STUDIO_THEMES ? intl.formatMessage(studioThemeMessages[themeId as StudioThemeId].name) : themeId,
    [intl]
  );

  const promptReady = useCallback(
    (watch: PublishWatch, downloadUrl: string | null) => {
      const name = assetName(watch.themeId);
      const actionId = `${ACTION_PREFIX}${watch.kitAssetUuid}`;
      registerToastAction(actionId, () =>
        openPublishDialog({
          accountId: watch.accountId,
          kitAssetUuid: watch.kitAssetUuid,
          catalogItemUuid: watch.catalogItemUuid,
          channel: 'youtube',
          assetName: name,
          justReady: true,
          downloadUrl,
        })
      );
      dispatch(
        showToast({
          type: ToastType.success,
          description: intl.formatMessage(publishMessages.promptReady, { asset: name }),
          autoHide: false,
          uniqueId: actionId,
          action: { label: intl.formatMessage(publishMessages.promptTitle, { channel: 'YouTube' }), actionId },
        })
      );
    },
    [assetName, dispatch, intl]
  );

  const check = useCallback(
    async (watch: PublishWatch) => {
      const { data: answer } = await dispatch(
        fymAPI.endpoints.getReleaseKit.initiate(
          { accountId: watch.accountId, catalogItemUuid: watch.catalogItemUuid },
          { forceRefetch: true, subscribe: false }
        )
      );
      const asset = [...(answer?.newest ?? []), ...(answer?.assets ?? [])].find(
        (one) => one.uuid === watch.kitAssetUuid
      );
      if (asset === undefined || (asset.status !== 'ready' && asset.status !== 'failed')) {
        return;
      }
      removePublishWatch(watch.kitAssetUuid);
      if (asset.status === 'ready') {
        promptReady(watch, null);
      }
    },
    [dispatch, promptReady]
  );

  useRealtimeEvent('kit.asset.updated', (event) => {
    const watch = readPublishWatches().find((one) => one.kitAssetUuid === event.assetUuid);
    if (watch !== undefined) {
      check(watch).catch(console.error);
    }
  });

  // A watch of an asset that finished while the tab was away: check each one once at start.
  useEffect(() => {
    readPublishWatches().forEach((watch) => check(watch).catch(console.error));
  }, [check]);

  return <PublishDialog request={request} />;
};

export const PublishHost = TypedMemo(PublishHostComponent);
